/**
 * 网络状态监控服务
 * 用于监控网络连接状态，提供在线状态检查和事件通知
 */

export type NetworkEventType = 'online' | 'offline';
export type NetworkEventListener = (status: boolean) => void;

class NetworkService {
  private listeners: Map<string, NetworkEventListener[]> = new Map();
  private isOnline: boolean = true;
  
  constructor() {
    this.initialize();
  }
  
  /**
   * 初始化网络状态监听
   */
  private initialize(): void {
    // 初始化当前状态
    this.isOnline = navigator.onLine;
    
    // 添加网络状态事件监听
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }
  
  /**
   * 处理在线事件
   */
  private handleOnline = (): void => {
    this.isOnline = true;
    this.notifyListeners('online', true);
  };
  
  /**
   * 处理离线事件
   */
  private handleOffline = (): void => {
    this.isOnline = false;
    this.notifyListeners('offline', false);
  };
  
  /**
   * 通知所有监听器网络状态变化
   * @param event 事件类型
   * @param status 网络状态
   */
  private notifyListeners(event: NetworkEventType, status: boolean): void {
    const listeners = this.listeners.get(event) || [];
    listeners.forEach(listener => {
      try {
        listener(status);
      } catch (error) {
        console.error(`Error notifying listener for ${event} event:`, error);
      }
    });
  }
  
  /**
   * 检查当前是否在线
   * @returns boolean
   */
  isNetworkAvailable(): boolean {
    return this.isOnline;
  }
  
  /**
   * 主动检查网络连接
   * @param url 要检查的URL，默认为谷歌
   * @param timeout 超时时间，默认5秒
   * @returns Promise<boolean>
   */
  async testConnection(url: string = 'https://www.google.com', timeout: number = 5000): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const response = await fetch(url, {
        method: 'HEAD',
        mode: 'no-cors',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      return true;
    } catch (error) {
      console.warn('Network connection test failed:', error);
      return false;
    }
  }
  
  /**
   * 添加网络状态变化监听器
   * @param eventType 事件类型，'online' 或 'offline'
   * @param listener 监听器回调函数
   */
  addEventListener(eventType: NetworkEventType, listener: NetworkEventListener): void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    
    const listeners = this.listeners.get(eventType)!;
    listeners.push(listener);
  }
  
  /**
   * 移除网络状态变化监听器
   * @param eventType 事件类型，'online' 或 'offline'
   * @param listener 监听器回调函数
   */
  removeEventListener(eventType: NetworkEventType, listener: NetworkEventListener): void {
    if (!this.listeners.has(eventType)) return;
    
    const listeners = this.listeners.get(eventType)!;
    this.listeners.set(
      eventType, 
      listeners.filter(l => l !== listener)
    );
  }
  
  /**
   * 销毁服务，清理事件监听
   */
  destroy(): void {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    this.listeners.clear();
  }
}

const networkService = new NetworkService();
export default networkService; 